import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { Divider, Form, Input, Modal } from 'antd'
import clsx from 'clsx'

import B2becButton from '@/components/B2becButton'
import B2becSpinner from '@/components/B2becSpinner'
import { CustomFormItem } from '@/components/B2bUserForm'
import { ChevronLeftIcon, CloseIcon } from '@/components/Icons'
import { NotificationManager } from '@/components/Notification'
import { WishlistService } from '@/services'
import { useAppDispatch } from '@/store/hook'
import { updateWishlistSharing } from '@/store/slices/wishlistSlice'
import { useAsync } from '@/hooks'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { STATUS_CODE } from '@/libs/constants'
import BREAKPOINTS from '@/libs/constants/breakpoints'
import { isValidArray } from '@/libs/utils/array'

import styles from './EditWishlistModal.module.scss'
import {
  shareWishlistToCompany,
  shareWishlistToUsers,
  withdrawSharingWishlistToCompany,
} from './helper'
import { SharingWishlistForm } from './SharingWishlistForm'

const EditWishlistModal = (props) => {
  const {
    ownerId,
    wishlistId,
    shouldShowModal,
    setShouldShowModal,
    shouldShowSharing,
    onRenameWishlist,
    wishlistName = '',
  } = props
  const [form] = Form.useForm()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const pathname = location.pathname

  const [isFetching, setIsFetching] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const initialValues = {
    name: '',
    collaborators: [],
    sharedWithCompany: false,
    ownerId: '',
    wishlistId: '',
  }
  const [currentValues, setCurrentValues] = useState(initialValues)

  const isMounted = useRef(null)
  const isLargerThan992 = useMediaQuery(`(min-width:${BREAKPOINTS.lg}px)`)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  const handleCancel = () => {
    setShouldShowModal(false)
  }

  const getWishlistSharedDetail = useCallback(() => {
    return WishlistService.getWishlistSharedDetail(wishlistId)
  }, [wishlistId])

  const { execute: executeGetWishlistSharedDetail } = useAsync(
    getWishlistSharedDetail,
    false,
  )

  const hasFetchedRef = useRef(false)

  useEffect(() => {
    if (hasFetchedRef.current) return

    if (shouldShowSharing) {
      hasFetchedRef.current = true
      executeGetWishlistSharedDetail().then(({ response, error }) => {
        if (response?.status === STATUS_CODE.SUCCESS) {
          const wishlistItem = response?.data
          const formValues = {
            name: wishlistItem?.wishlistName,
            collaborators: wishlistItem?.collaborators,
            sharedWithCompany: wishlistItem?.sharedWithCompany,
            ownerId: wishlistItem?.wishlistId,
            wishlistId: wishlistItem?.wishlistId,
          }
          form.setFieldsValue(formValues)
          setCurrentValues(formValues)
        }
        if (error) {
          NotificationManager.error({
            message: 'notification.error.fetch',
          })
        }
        setIsFetching(false)
      })
    } else {
      form.setFieldsValue({
        name: wishlistName,
      })
      setIsFetching(false)
    }
  }, [shouldShowSharing, wishlistName, executeGetWishlistSharedDetail, form])

  const handleSubmitResults = (results, newName, newCollaborators) => {
    const rejectedResponses = results
      .filter((result) => result.status === 'rejected')
      .map((result) => result.reason)

    if (isValidArray(rejectedResponses)) {
      console.error('Errors: ', rejectedResponses)

      NotificationManager.error({
        message: 'notification.error.submit',
      })
    } else {
      NotificationManager.success({
        message: 'notification.success.editWishlist',
      })
      dispatch(
        updateWishlistSharing({
          ownerId,
          wishlistId,
          wishlistName: newName,
          collaboratorCount: newCollaborators?.length,
          site: pathname.includes('wishlists') ? 'overview' : 'detail',
        }),
      )
    }
  }

  const submitShareWishlist = async (values) => {
    const {
      name: newName,
      collaborators: newCollaborators,
      sharedWithCompany: newSharedWithCompany,
    } = values

    const { collaborators, sharedWithCompany } = currentValues

    const promises = []

    if (
      !newSharedWithCompany
      && JSON.stringify(collaborators) !== JSON.stringify(newCollaborators)
    ) {
      const userIdList = newCollaborators.map((item) => item?.userId)
      const shareWishlistToUsersPromise = shareWishlistToUsers({
        wishlistId,
        userIds: userIdList,
      })

      promises.push(shareWishlistToUsersPromise)
    }

    if (newSharedWithCompany !== sharedWithCompany) {
      if (newSharedWithCompany) {
        const shareWishlistToCompanyPromise = shareWishlistToCompany(wishlistId)

        promises.push(shareWishlistToCompanyPromise)
      } else {
        const withdrawSharingWishlistToCompanyPromise = withdrawSharingWishlistToCompany(wishlistId)

        promises.push(withdrawSharingWishlistToCompanyPromise)
      }
    }

    const results = await Promise.allSettled(promises)

    if (isMounted.current) {
      handleSubmitResults(results, newName, newCollaborators)
    }
  }

  const submitRename = async (values) => {
    onRenameWishlist(values.name)
  }

  const handleSubmit = (values) => {
    if (isMounted) {
      setIsSubmitting(true)
      submitRename(values)
      if (shouldShowSharing) {
        submitShareWishlist(values)
      }
      setIsSubmitting(false)
      setShouldShowModal(false)
    }
  }

  const renderHeaderSection = () => {
    if (!isLargerThan992) {
      return (
        <>
          <div className={styles.title}>
            <button
              className="button-as-link"
              type="button"
              onClick={handleCancel}
            >
              <ChevronLeftIcon /> {t('buttonTexts.cancel')}
            </button>
            <button
              className="button-as-link"
              type="button"
              onClick={() => {
                form.submit()
              }}
            >
              {t('buttonTexts.save')}
            </button>
          </div>
          <Divider className={styles.divider} />
        </>
      )
    }

    return (
      <>
        <h1 className={styles.title}>
          {t('wishlist.modal.editWishlist.title')}
        </h1>
        <p className="my-2">{t('wishlist.modal.editWishlist.description')}</p>
      </>
    )
  }

  return (
    <Modal
      width={isLargerThan992 ? 540 : '100%'}
      className={styles.model__wrapper}
      centered
      closable={isLargerThan992}
      destroyOnHidden
      footer={null}
      open={shouldShowModal}
      closeIcon={<CloseIcon />}
      onCancel={handleCancel}
    >
      <B2becSpinner isLoading={isFetching} />
      <Form
        form={form}
        initialValues={initialValues}
        layout="vertical"
        onFinish={handleSubmit}
      >
        {renderHeaderSection()}
        <CustomFormItem
          className={clsx('custom-form-item', styles.inputField)}
          label="changeName"
          name="name"
          rules={[
            {
              required: true,
              message: t('checkout.submitErrors.invalidMissingName'),
            },
          ]}
        >
          <Input data-testid="name" placeholder={t('checkout.typeHere')} />
        </CustomFormItem>

        {shouldShowSharing && (
          <SharingWishlistForm
            form={form}
            sharedWithCompany={currentValues.sharedWithCompany}
          />
        )}

        {isLargerThan992 && (
          <div className={styles.footer}>
            <B2becButton onClick={handleCancel} ghost>
              {t('buttonTexts.cancel')}
            </B2becButton>
            <B2becButton
              loading={isSubmitting}
              onClick={() => {
                form.submit()
              }}
            >
              {t('buttonTexts.save')}
            </B2becButton>
          </div>
        )}
      </Form>
    </Modal>
  )
}
export default EditWishlistModal
